// WMS-IV Logisches Gedächtnis (German) — calc wrappers that map raw-score
// input to a scoresheet entry (resultLoc + resultData + resultText +
// evaluation). DE-only tests, two booklets:
//   version "ab" (Geschichten A & B) -> rows G59 / G60 / G61
//   version "bc" (Geschichten B & C) -> rows G62 / G63 / G64

import { computeEvaluation } from "./scoringEval";
import {
  scoreLGI,
  scoreLGII,
  scoreLGIIRecog,
} from "db/WMS-IV-DE/wms-iv-de-scoring";

const ROWS = {
  lgI:        { ab: "G59", bc: "G62" },
  lgII:       { ab: "G60", bc: "G63" },
  lgIIRecog:  { ab: "G61", bc: "G64" },
};

// Map a parquet PR-band string to a value computeEvaluation's classifier can
// read. "X-Y" ranges are passed through as-is: computeEvaluation now spans
// the mark across every ASNP category the range touches instead of pinning
// it to one bound (previously this used the midpoint as a single-point
// stand-in to avoid over-pathologizing patients in the upper half of the
// band).
//   "<= N"  -> N - 0.5   ("< N+1" equivalent)
//   ">= N"  -> N + 0.5   ("> N-1" equivalent)
//   "X-Y"   -> unchanged
//   plain N -> N
function prBandRawValue(band) {
  if (band == null) return band;
  const s = String(band).trim();
  const le = s.match(/^<=\s*(\d+(?:\.\d+)?)/);
  if (le) return `< ${parseFloat(le[1]) + 1}`;
  const ge = s.match(/^>=\s*(\d+(?:\.\d+)?)/);
  if (ge) return `> ${parseFloat(ge[1]) - 1}`;
  return s;
}

async function calcWPTest(scorerPromise, resultLoc) {
  const r = await scorerPromise;
  if (r.error) return { error: r.error };
  // Label "NS" so the ASNP classifier reuses the WAIS scaled-score thresholds
  // (WP and NS share the mean-10 / SD-3 scale).
  const resultData = [{ name: "nS", label: "NS", value: r.wp }];
  const resultText = `WP= ${r.wp}`;
  const evaluation = computeEvaluation(resultData, resultLoc);
  return { resultLoc, resultData, resultText, evaluation };
}

export function calcLGIScore(rawScore, age, version = "ab") {
  return calcWPTest(scoreLGI(rawScore, age, version), ROWS.lgI[version]);
}

export function calcLGIIScore(rawScore, age, version = "ab") {
  return calcWPTest(scoreLGII(rawScore, age, version), ROWS.lgII[version]);
}

export async function calcLGIIRecogScore(rawScore, age, version = "ab") {
  const r = await scoreLGIIRecog(rawScore, age, version);
  if (r.error) return { error: r.error };
  const resultLoc = ROWS.lgIIRecog[version];
  const resultData = [{
    name: "pC",
    label: "PC",
    value: r.pr,
    rawValue: prBandRawValue(r.pr),
  }];
  const resultText = `PR= ${r.pr}`;
  const evaluation = computeEvaluation(resultData, resultLoc);
  return { resultLoc, resultData, resultText, evaluation };
}
